<template>
  <FilteringFacets class="margin-top-l" :facets="facets" :sort-by="sortBy" @selected-criteria="handleChange" />
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { getAvailable } from '#utils/productCardTransform';
import filterAndSortWishlist from '#composables/useWishlistFilterSort';

const emit = defineEmits(['filter-change']);
const filteringFacetsStore = useFilteringFacetsStore();
const { currentWishlistItems, wishlistGuid } = useWishlistDetails();

// Track previous values to detect changes
let prevSelectedFilters = [];
let prevSortValue = null;

const isOnSale = (item) => !!item.upc?.price?.onSale;
const hasPromotions = (item) => !!item.promotions?.length;

const filterCounts = ref({
  'in-stock': 0,
  'on-sale': 0,
  'promotional-offers': 0,
});

const updateFilterCounts = () => {
  const counts = {
    'in-stock': 0,
    'on-sale': 0,
    'promotional-offers': 0,
  };

  currentWishlistItems.value?.forEach((item) => {
    if (getAvailable(item)) counts['in-stock'] += 1;
    if (isOnSale(item)) counts['on-sale'] += 1;
    if (hasPromotions(item)) counts['promotional-offers'] += 1;
  });

  filterCounts.value = counts;
};

// Watch for changes in currentWishlistItems
watch(
  () => currentWishlistItems.value,
  (newItems) => {
    if (newItems) {
      updateFilterCounts();
    }
  },
  { immediate: true },
);

// Initial calculation
updateFilterCounts();

const facets = computed(() => [
  {
    name: 'Filter',
    displayName: 'Filter',
    facetType: 'MULTISELECTDEFAULT',
    selectedValues: [],
    values: [
      {
        label: `In stock (${filterCounts.value['in-stock']})`,
        value: 'in-stock',
      },
      {
        label: `Sale items (${filterCounts.value['on-sale']})`,
        value: 'on-sale',
      },
      {
        label: `Promotional offers (${filterCounts.value['promotional-offers']})`,
        value: 'promotional-offers',
      },
    ],
  },
]);

const sortBy = [
  {
    value: 'RECENTLY_ADDED',
    displayValue: 'Recently added',
  },
  {
    value: 'PRICE_LOW_TO_HIGH',
    displayValue: 'Price: Low to High',
  },
  {
    value: 'PRICE_HIGH_TO_LOW',
    displayValue: 'Price: High to Low',
  },
  {
    value: 'CUSTOMER_TOP_RATED',
    displayValue: 'Customer top rated',
  },
];

const updateUrl = (selected) => {
  const params = new URLSearchParams(window.location.search);
  const filterFacet = selected.allSelected.facets?.find((f) => f.name === 'Filter');

  if (filterFacet?.selectedValues.length > 0) {
    params.set('filter', filterFacet.selectedValues.join(','));
  } else {
    params.delete('filter');
  }

  if (selected.allSelected.sortBy) {
    params.set('sortBy', selected.allSelected.sortBy);
  } else {
    params.delete('sortBy');
  }

  const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
  window.history.pushState({}, '', newUrl);
};

const handleChange = (selected) => {
  updateUrl(selected);

  const filterFacet = selected.allSelected.facets?.find((f) => f.name === 'Filter');
  const selectedFilters = filterFacet?.selectedValues || [];
  const sortValue = selected.allSelected.sortBy;

  const filtersChanged = JSON.stringify(selectedFilters) !== JSON.stringify(prevSelectedFilters);
  const sortChanged = sortValue !== prevSortValue;

  if (!filtersChanged && !sortChanged) {
    return;
  }

  // Defer heavy list filtering to the next macrotask.
  setTimeout(() => {
    const filterChecks = {
      'in-stock': (item) => getAvailable(item),
      'on-sale': (item) => isOnSale(item),
      'promotional-offers': (item) => hasPromotions(item),
    };
    const filteredItems = filterAndSortWishlist(currentWishlistItems.value, selectedFilters, sortValue, filterChecks);

    if (filtersChanged || sortChanged) {
      const tagParams = {
        event_name: 'wishlist sort/filter:apply',
        link_name: 'wishlist sort/filter:apply',
        wishlist_id: wishlistGuid.value,
        wishlist_sort_filter: `sort=${sortValue || ''}:filter=${selectedFilters.join(',')}`,
      };

      // Only fire tag if we have actual changes
      if (filtersChanged || sortChanged) {
        fireTag('link', tagParams);

        // Update previous values
        prevSelectedFilters = [...selectedFilters];
        prevSortValue = sortValue;
      }
    }

    emit('filter-change', filteredItems, { selectedFilters, sortValue });
  }, 0);
};

const setFilterAndSortFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const filterParam = params.get('filter');
  const sortByParam = params.get('sortBy');

  if (sortByParam) {
    filteringFacetsStore.updateSortBySelected(sortByParam);
  }

  if (filterParam) {
    filteringFacetsStore.replaceSelectedValues({
      name: 'Filter',
      selectedValues: filterParam.split(','),
    });
  }

  if (filterParam || sortByParam) {
    filteringFacetsStore.getAllSelectedCriteria();
  }
};

onMounted(() => {
  setFilterAndSortFromUrl();
});

onUpdated(() => {
  setFilterAndSortFromUrl();
});
</script>
